/**
 * Admin API Service
 * Provides functions for all admin operations:
 * - Admin status
 * - Tenant management
 * - Propel org management
 * - Admin user management
 * - Audit logs
 */

const BASE_URL = '/api/admin';

/**
 * Helper to make admin API requests
 */
async function request(endpoint, options = {}) {
  const url = `${BASE_URL}${endpoint}`;
  const defaultOptions = {
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
  };

  const merged = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...(options.headers || {})
    }
  };

  const response = await fetch(url, merged);

  // Handle response
  if (!response.ok) {
    let errorMessage = `API Error: ${response.status} ${response.statusText}`;

    try {
      const errorData = await response.json();
      if (errorData && (errorData.message || errorData.error)) {
        errorMessage = errorData.message || errorData.error;
      }
    } catch (e) {
      console.warn('Could not parse error response as JSON:', e);
    }

    throw new Error(errorMessage);
  }

  // Return JSON response
  return await response.json();
}

// ===== ADMIN STATUS =====

/**
 * Check if current user has admin access
 * @returns {Promise<{isAdmin: boolean, user: Object}>}
 */
export async function checkAdminStatus() {
  return await request('/status');
}

// ===== TENANT MANAGEMENT =====

/**
 * Get all tenants
 * @param {boolean} includeInactive - Include inactive tenants
 * @returns {Promise<{tenants: Array}>}
 */
export async function getTenants(includeInactive = false) {
  const query = includeInactive ? '?includeInactive=true' : '';
  return await request(`/tenants${query}`);
}

/**
 * Get tenant by ID
 * @param {number} id - Tenant ID
 * @returns {Promise<{tenant: Object}>}
 */
export async function getTenant(id) {
  return await request(`/tenants/${id}`);
}

/**
 * Create new tenant
 * @param {Object} data - Tenant data
 * @returns {Promise<{message: string, tenant: Object}>}
 */
export async function createTenant(data) {
  return await request('/tenants', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

/**
 * Update tenant
 * @param {number} id - Tenant ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{message: string, tenant: Object}>}
 */
export async function updateTenant(id, updates) {
  return await request(`/tenants/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  });
}

/**
 * Deactivate tenant
 * @param {number} id - Tenant ID
 * @returns {Promise<{message: string}>}
 */
export async function deactivateTenant(id) {
  return await request(`/tenants/${id}`, {
    method: 'DELETE'
  });
}

/**
 * Reactivate tenant
 * @param {number} id - Tenant ID
 * @returns {Promise<{message: string}>}
 */
export async function activateTenant(id) {
  return await request(`/tenants/${id}/activate`, {
    method: 'POST'
  });
}

// ===== PROPEL ORG MANAGEMENT =====

/**
 * Get all Propel organizations
 * @param {boolean} includeInactive - Include inactive Propel orgs
 * @returns {Promise<{propelOrgs: Array}>}
 */
export async function getPropelOrgs(includeInactive = false) {
  const query = includeInactive ? '?includeInactive=true' : '';
  return await request(`/propel-orgs${query}`);
}

/**
 * Get Propel orgs by tenant ID
 * @param {number} tenantId - Tenant ID
 * @returns {Promise<{propelOrgs: Array}>}
 */
export async function getPropelOrgsByTenant(tenantId) {
  return await request(`/propel-orgs/by-tenant/${tenantId}`);
}

/**
 * Get Propel org by ID
 * @param {number} id - Propel org ID
 * @returns {Promise<{propelOrg: Object}>}
 */
export async function getPropelOrg(id) {
  return await request(`/propel-orgs/${id}`);
}

/**
 * Create new Propel organization
 * @param {Object} data - Propel org data
 * @returns {Promise<{message: string, propelOrg: Object}>}
 */
export async function createPropelOrg(data) {
  return await request('/propel-orgs', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

/**
 * Update Propel organization
 * @param {number} id - Propel org ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{message: string, propelOrg: Object}>}
 */
export async function updatePropelOrg(id, updates) {
  return await request(`/propel-orgs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  });
}

/**
 * Set Propel org as default
 * @param {number} id - Propel org ID
 * @returns {Promise<{message: string}>}
 */
export async function setDefaultPropelOrg(id) {
  return await request(`/propel-orgs/${id}/set-default`, {
    method: 'POST'
  });
}

/**
 * Deactivate Propel organization
 * @param {number} id - Propel org ID
 * @returns {Promise<{message: string}>}
 */
export async function deactivatePropelOrg(id) {
  return await request(`/propel-orgs/${id}`, {
    method: 'DELETE'
  });
}

/**
 * Reactivate Propel organization
 * @param {number} id - Propel org ID
 * @returns {Promise<{message: string}>}
 */
export async function activatePropelOrg(id) {
  return await request(`/propel-orgs/${id}/activate`, {
    method: 'POST'
  });
}

// ===== ADMIN USER MANAGEMENT =====

/**
 * Get all admin users
 * @param {boolean} includeInactive - Include inactive admin users
 * @returns {Promise<{adminUsers: Array}>}
 */
export async function getAdminUsers(includeInactive = false) {
  const query = includeInactive ? '?includeInactive=true' : '';
  return await request(`/admin-users${query}`);
}

/**
 * Grant admin access to a user
 * @param {Object} data - Admin user data
 * @returns {Promise<{message: string, adminUser: Object}>}
 */
export async function grantAdminAccess(data) {
  return await request('/admin-users', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

/**
 * Update admin user
 * @param {number} id - Admin user ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{message: string, adminUser: Object}>}
 */
export async function updateAdminUser(id, updates) {
  return await request(`/admin-users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  });
}

/**
 * Revoke admin access
 * @param {number} id - Admin user ID
 * @returns {Promise<{message: string}>}
 */
export async function revokeAdminAccess(id) {
  return await request(`/admin-users/${id}`, {
    method: 'DELETE'
  });
}

// ===== AUDIT LOGS =====

/**
 * Get audit logs
 * @param {Object} params - Query parameters (limit, offset, filters)
 * @returns {Promise<{auditLogs: Array, pagination: Object}>}
 */
export async function getAuditLogs(params = {}) {
  const queryParams = new URLSearchParams();

  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);
  if (params.adminUserId) queryParams.append('adminUserId', params.adminUserId);
  if (params.action) queryParams.append('action', params.action);
  if (params.resourceType) queryParams.append('resourceType', params.resourceType);
  if (params.resourceId) queryParams.append('resourceId', params.resourceId);

  const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
  return await request(`/audit-logs${query}`);
}

/**
 * Get audit logs for a specific resource
 * @param {string} type - Resource type (e.g., 'tenant', 'propel_org')
 * @param {string} id - Resource ID
 * @param {number} limit - Maximum number of logs to return
 * @returns {Promise<{auditLogs: Array}>}
 */
export async function getAuditLogsForResource(type, id, limit = 100) {
  const query = limit ? `?limit=${limit}` : '';
  return await request(`/audit-logs/resource/${type}/${id}${query}`);
}

export default {
  checkAdminStatus,
  getTenants,
  getTenant,
  createTenant,
  updateTenant,
  deactivateTenant,
  activateTenant,
  getPropelOrgs,
  getPropelOrgsByTenant,
  getPropelOrg,
  createPropelOrg,
  updatePropelOrg,
  setDefaultPropelOrg,
  deactivatePropelOrg,
  activatePropelOrg,
  getAdminUsers,
  grantAdminAccess,
  updateAdminUser,
  revokeAdminAccess,
  getAuditLogs,
  getAuditLogsForResource
};
